/* eslint-disable no-extra-boolean-cast */
/* eslint-disable no-console */
/* eslint-disable import/prefer-default-export */
/*eslint-env browser */
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch'
import { isEmpty } from 'lodash'
import uuid from 'uuid'
import 'whatwg-fetch'
import { storagePrefix } from '@src/constants/constants'
import { FETCH_STATUS } from '@src/constants/events'
import GlobalEvents from '@src/utils/custom.event'
import { refreshToken } from '@src/utils/jwt.token'
import { AppStorage } from '@src/utils/storage.helpers'
import { partialResult } from '@src/utils/toast'
import { handleErrors, objectToQueryString, showErrorMessage } from './helpers'
import { handlePending, HandlePendingOptions } from './helpers/pending'

export type { APIResponse } from './models'

// In case when browser's js returns protocol with
// ending ':' char - this char should be cut of
const getBrowserProtocol = () =>
  (window?.location?.protocol || 'http').replace(/:/g, '')

const PROTOCOL = window.__PROTOCOL__ || getBrowserProtocol()
const API_SERVER = window.__HOST__ || window?.location?.host
const ENV_WEB_PORT = window.__WEB_PORT__

export interface APIRequest<T, V, R> {
  params: T
  queryVars?: V
  options?: RequestOptions
  makeRequestOptions?: Partial<MakeRequestOptions<R>>
  urlRequest?: string
}

export type HTTPMethod =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'HEAD'
  | 'DELETE'
  | 'PATCH'
  | 'TRACE'
  | 'OPTIONS'
  | 'CONNECT'

const storage = new AppStorage(storagePrefix, sessionStorage)

/**
 * @description
 * Makes fetch request to Endpoint
 *
 * @param {Object} options
 * @param {String} options.port port
 * @param {String} options.url url
 * @param {String} options.method method type {GET,POST,DELETE,PUT,PATCH}
 * @param {*} options.options options
 * @param {String} options.params params which are passing to request
 *
 * @returns Promise<Response> of fetch
 */

export type RequestOptions = RequestInit & {
  method?: HTTPMethod | string
  headers?: RequestHeadersInit
}

type RequestHeadersInit = HeadersInit & {
  Authorization?: string
}

export interface MakeRequestOptions<Params> {
  port?: string
  url: string
  method: HTTPMethod | string
  reqOptions?: RequestOptions
  params?: Params
  useTransactionId?: boolean
  pendingStatus?: boolean
  handleResponseErrors?: (response: Response) => void
}

interface MakeExternalRequestOptions<Params>
  extends Omit<MakeRequestOptions<Params>, 'port' | 'useTransactionId'> {}

interface FetchData {
  url: string
  init?: RequestInit
}

interface FetchWrapper<T> {
  data: FetchData
  useToken: boolean
  pendingOptions: HandlePendingOptions<T>
  handleResponseErrors?: (response: Response) => void
}

export function partialResultInterceptor(res) {
  // Checking for Partial Results header - if so display toast message
  // Mocking as string until backend work is completed
  if (res?.resp_hdr && res.resp_hdr === 'x-sp-partial-results') {
    partialResult(res.resp_hdr)
  }
  return res
}

const getUrlRequest = (url: string, query?: string, port?: string) => {
  const urlPort = !isEmpty(port) ? `:${port}` : ''
  return `${PROTOCOL}://${API_SERVER}${urlPort}${url}${query}`
}

export const makeRequest = <T>(options: MakeRequestOptions<T>) => {
  const {
    port,
    url,
    method,
    reqOptions = {},
    params,
    handleResponseErrors,
    useTransactionId = true
  } = options
  let query = ''

  const pendingOptions: HandlePendingOptions<T> = {
    requestFetchUID: uuid(),
    isPending: true,
    // pendingStatus default value is true
    makeRequestOptions: { pendingStatus: true, ...options }
  }
  const tid = useTransactionId ? pendingOptions?.requestFetchUID : null
  if (params) {
    query = objectToQueryString(params, tid)
  }
  const defaultOptions: RequestOptions = {
    referrerPolicy: 'strict-origin-when-cross-origin',
    method: method || 'GET',
    //mode: 'no-cors',
    headers: {
      'Referrer-Policy': 'strict-origin-when-cross-origin',
      Accept: 'application/json'
    },
    signal: window.ABORT_CONTROLLER?.signal
  }

  let servicePort: string
  if (typeof ENV_WEB_PORT !== 'undefined') {
    // if an the WEB_PORT environment variable is set
    // use the value of that env variable for all ports
    servicePort = ENV_WEB_PORT
  } else {
    // else, use the value of the passed in port argument
    servicePort = port
  }

  const urlRequest = getUrlRequest(url, query, servicePort)

  return fetchWrapper({
    useToken: true,
    data: {
      url: urlRequest,
      init: {
        ...defaultOptions,
        ...reqOptions,
        headers: {
          ...defaultOptions?.headers,
          ...reqOptions?.headers
        }
      }
    },
    pendingOptions,
    handleResponseErrors
  })
}

export const makeExternalRequest = <T>(
  options: MakeExternalRequestOptions<T>
) => {
  const { url, method, reqOptions = {}, handleResponseErrors } = options

  const pendingOptions: HandlePendingOptions<T> = {
    requestFetchUID: uuid(),
    isPending: true,
    // pendingStatus default value is true
    makeRequestOptions: { pendingStatus: true, ...options }
  }
  const defaultOptions: RequestOptions = {
    method: method || 'GET',
    headers: {
      Accept: 'application/json'
    },
    signal: window.ABORT_CONTROLLER?.signal
  }

  return fetchWrapper({
    useToken: true,
    data: {
      url,
      init: {
        ...defaultOptions,
        ...reqOptions,
        headers: {
          ...defaultOptions?.headers,
          ...reqOptions?.headers
        }
      }
    },
    pendingOptions,
    handleResponseErrors
  })
}

function fetchWithToken(input: RequestInfo, options?: RequestOptions) {
  const init: RequestOptions = { ...options }
  const oauthToken = storage.get('oauthToken')
  // do not pass token if anonymous request
  if (!!oauthToken) {
    init.headers.Authorization = `bearer ${oauthToken}`
    try {
      refreshToken('oauthToken', () => {
        init.signal = null
      })
    } catch (ex) {
      init.signal = null
    }
  }
  return fetch(input, init)
}

function fetchWrapper<T>({
  data: { url, init },
  useToken,
  pendingOptions,
  handleResponseErrors
}: FetchWrapper<T>) {
  handlePending({ ...pendingOptions })
  const request = useToken ? fetchWithToken : fetch

  return request(url, init)
    .then(
      handleErrors(() => {
        handlePending({ ...pendingOptions, isPending: false })
      }, handleResponseErrors)
    )
    .then(res => partialResultInterceptor(res))
    .then(res => {
      GlobalEvents.dispatch(FETCH_STATUS, { status: 'success' })
      setTimeout(() => {
        handlePending({ ...pendingOptions, isPending: false })
      }, 100)
      return res
    })
    .catch(error => {
      handlePending({ ...pendingOptions, isPending: false })
      if (error?.name === 'AbortError') {
        console.log('Fetch aborted')
        GlobalEvents.dispatch(FETCH_STATUS, { status: 'abort' })
        return error
      } else if (error.message === 'Failed to fetch') {
        GlobalEvents.dispatch(FETCH_STATUS, { status: 'failed' })
        throw error
      } else {
        showErrorMessage(error)
        GlobalEvents.dispatch(FETCH_STATUS, { status: 'error' })
        throw error
      }
    })
}
